import {Route, Switch, Redirect} from 'react-router-dom';
// @ts-expect-error
import type {Location} from 'react-router-dom';

import type {User} from 'nutshell-core/types';

import {setBodyId} from '../../dom';
import {CallsListPage} from '../../ui/calls/calls-list-page';
import {EmailTemplatesPage} from '../../ui/email/templates/email-templates-page';
import {TemplateGroupsPage} from '../../ui/email/templates/template-groups/template-groups-page';
import {ProspectorListPage} from '../../ui/email-marketing/lead-generation/prospector/prospector-page';
import {MeetListPage} from '../../ui/external-meetings/meet/meet-list-page';
import {TeamsListPage} from '../../ui/external-meetings/teams/teams-list-page';
import {ZoomListPage} from '../../ui/external-meetings/zoom/zoom-list-page';
import {PageContainer} from '../../ui/layout/page-container';
import {PageWrapper} from '../../ui/layout/page-wrapper';
import {SalesQuotaFeatureGate} from '../../ui/limits/feature-gates/sales-quota-feature-gate';
import {MasterDashboard} from '../../ui/master-dashboard/master-dashboard';
import {getCurrentFiscalYear, getYears} from '../../ui/sales/quotas/helpers';
import {SalesQuotasPage} from '../../ui/sales/quotas/sales-quotas-page';
import {SalesSidebar} from '../../ui/sales/sales-sidebar';
import {SalesAutomationRoot} from '../../ui/sales-automation/sales-automation-root';
import {SchedulerRoot} from '../../ui/scheduler/scheduler-root';

import {ListPageRoute} from '../shared/list-page-route';
import {RefreshingRoute} from '../shared/refreshing-route';
import {RestrictedListPageRoute} from '../shared/restricted-list-page-route';
import {RestrictedRoute} from '../shared/restricted-route';

import {
    LeadOutcomesPageStandalone,
    SalesQuotasPageStandalone,
    ActivityQuotasPageStandalone,
    QuoteTemplatePageStandalone,
    QuoteManagePageStandalone,
    InvoiceTemplatePageStandalone,
    InvoiceOverviewPageStandalone,
    QuoteAndInvoiceUpgradePageStandalone,
    ProductsPageStandalone,
    CategoriesPageStandalone,
} from './standalone';

function shouldHideSalesSidebar(pathname: string): boolean {
    // Hide secondary sidebar on the Lead Layout Customizer full-screen editor
    // Example path: /sales/pipelines/1-stagesets/customize-layout
    return /^\/sales\/pipelines\/\d+-stagesets\/customize-layout$/.test(pathname);
}

type Props = {
    prevLocation?: Location;
    user: User | null | undefined;
};

export function SalesRoot(props: Props) {
    // When we do not know what the user permissions are, we assume they have
    // access to everything. This avoids issues where our restricted routes
    // would redirect the user before we knew if they could access the page.
    // Once the user loads, we will properly redirect them, but this results in
    // an overall smoother experience.
    const canAccessSetup = props.user ? props.user.permissions.canAccessSetup : true;
    const canAccessCrm = props.user ? props.user.permissions.canAccessCrm : true;
    const canUseProspectorIq = props.user ? props.user.permissions.canUseProspectorIQ : true;
    const canUseSalesDocuments = props.user ? props.user.permissions.canUseSalesDocuments : true;

    return (
        <PageWrapper
            sidebar={
                shouldHideSalesSidebar(window.location.pathname) ? null : (
                    <SalesSidebar
                        canAccessSetup={canAccessSetup}
                        canUseProspectorIq={canUseProspectorIq}
                        canUseSalesDocuments={canUseSalesDocuments}
                    />
                )
            }
        >
            <Switch location={props.prevLocation || window.location}>
                <RefreshingRoute
                    path='/sales/dashboard'
                    render={() => renderDashboard(canAccessCrm)}
                />
                <RefreshingRoute path='/sales/scheduler' render={renderSchedulerPage} />
                <Redirect exact={true} from='/sales/calls' to='/sales/calls/unlogged-meetings' />
                <Route
                    path='/sales/calls/:tab'
                    exact={true}
                    render={(routeProps) => {
                        return (
                            <PageContainer noPadding={true} title='Calls'>
                                <CallsListPage {...routeProps} />
                            </PageContainer>
                        );
                    }}
                />
                <RefreshingRoute
                    path='/sales/prospectoriq'
                    render={() => renderProspectorIQPage(canUseProspectorIq)}
                />
                <Redirect
                    exact={true}
                    from='/sales/meetings/zoom'
                    to='/sales/meetings/zoom/unlogged-meetings'
                />
                <Route
                    path='/sales/meetings/zoom/:tab'
                    exact={true}
                    render={(routeProps) => {
                        return (
                            <PageContainer noPadding={true} title='Zoom'>
                                <ZoomListPage {...routeProps} />
                            </PageContainer>
                        );
                    }}
                />
                <Redirect
                    exact={true}
                    from='/sales/meetings/meet'
                    to='/sales/meetings/meet/unlogged-meetings'
                />
                <Route
                    path='/sales/meetings/meet/:tab'
                    exact={true}
                    render={(routeProps) => {
                        return (
                            <PageContainer noPadding={true} title='Google Meet'>
                                <MeetListPage {...routeProps} />
                            </PageContainer>
                        );
                    }}
                />
                <Redirect
                    exact={true}
                    from='/sales/meetings/teams'
                    to='/sales/meetings/teams/unlogged-meetings'
                />
                <Route
                    path='/sales/meetings/teams/:tab'
                    exact={true}
                    render={(routeProps) => {
                        return (
                            <PageContainer noPadding={true} title='Microsoft Teams'>
                                <TeamsListPage {...routeProps} />
                            </PageContainer>
                        );
                    }}
                />
                <Route
                    exact={true}
                    path='/sales/pipelines/:stagesetId/customize-layout'
                    render={renderSalesAutomationPage}
                />
                <RestrictedRoute
                    path='/sales/pipelines'
                    hasAccess={NutClientConfig.hasSalesProcessSetupOverhaulNUT7581}
                    redirectPath='/setup/stage'
                    render={renderSalesAutomationPage}
                />
                <Route
                    path='/sales/email/templates'
                    exact={true}
                    render={(renderProps) => {
                        return (
                            <ListPageRoute
                                path={renderProps.match.path}
                                defaultQuery={
                                    // Default view is user's own templates
                                    `?filter%5BjustMine%5D=true`
                                }
                                localStorageKey='email-template-groups'
                                includeHash={true}
                                component={TemplateGroupsPage}
                            />
                        );
                    }}
                />
                <Route
                    path='/sales/email/templates/:type/:action?'
                    render={(renderProps) => {
                        return <EmailTemplatesPage {...renderProps} />;
                    }}
                />

                <RestrictedRoute
                    path='/sales/quote-template'
                    render={(routeProps) => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list fixed-width';
                        }

                        setBodyId('quote-template');

                        // @ts-expect-error
                        return <QuoteTemplatePageStandalone search={routeProps.location.search} />;
                    }}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/quote-manage'
                />
                <Route
                    path='/sales/quote-manage'
                    exact={true}
                    render={(renderProps) => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list';
                        }

                        setBodyId('quote-manage');

                        // @ts-expect-error
                        return <QuoteManagePageStandalone search={renderProps.location.search} />;
                    }}
                />
                <Route
                    path='/sales/quote-overview'
                    exact={true}
                    render={(renderProps) => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list';
                        }

                        setBodyId('quote-overview');

                        // @ts-expect-error
                        return <QuoteManagePageStandalone search={renderProps.location.search} />;
                    }}
                />
                <RestrictedRoute
                    path='/sales/quote-upgrade'
                    render={() => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list fixed-width';
                        }

                        setBodyId('quote-upgrade');

                        return <QuoteAndInvoiceUpgradePageStandalone page='quote' />;
                    }}
                    hasAccess={NutClientConfig ? !NutClientConfig.hasQuotePlan : true}
                    redirectPath='/sales/quote-manage'
                />

                <RestrictedRoute
                    path='/sales/invoice-template'
                    render={(routeProps) => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list fixed-width';
                        }

                        setBodyId('invoice-template');

                        return (
                            // @ts-expect-error
                            <InvoiceTemplatePageStandalone search={routeProps.location.search} />
                        );
                    }}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/invoice-overview'
                />
                <Route
                    path='/sales/invoice-overview'
                    exact={true}
                    render={(renderProps) => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list';
                        }

                        setBodyId('invoice-overview');

                        return (
                            // @ts-expect-error
                            <InvoiceOverviewPageStandalone search={renderProps.location.search} />
                        );
                    }}
                />
                <RestrictedRoute
                    path='/sales/invoice-upgrade'
                    render={() => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list fixed-width';
                        }

                        setBodyId('invoice-upgrade');

                        return <QuoteAndInvoiceUpgradePageStandalone page='invoice' />;
                    }}
                    hasAccess={NutClientConfig ? !NutClientConfig.hasQuotePlan : true}
                    redirectPath='/sales/invoice-overview'
                />

                <RestrictedRoute
                    path='/sales/product/categories'
                    exact={true}
                    render={() => <CategoriesPageStandalone />}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/dashboard'
                />

                <RestrictedRoute
                    path='/sales/product'
                    exact={true}
                    render={() => <ProductsPageStandalone />}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/dashboard'
                />

                <RestrictedRoute
                    path='/sales/lead-outcomes'
                    render={(routeProps) => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list';
                        }

                        setBodyId('list-outcome');

                        return <LeadOutcomesPageStandalone search={routeProps.location.search} />;
                    }}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/dashboard'
                />
                <RestrictedRoute
                    path='/sales/sales-quotas/view/:quotaId'
                    render={(routeProps) => {
                        const body = document.querySelector('body');

                        setBodyId('quota-manager');

                        if (!NutClientConfig.hasQuotas) {
                            return <SalesQuotaFeatureGate />;
                        }

                        if (
                            NutClientConfig.hasQuotasV2FF &&
                            NutClientConfig.hasMigratedToQuotasV2
                        ) {
                            return <Redirect to='/sales/sales-quotas' />;
                        }

                        if (body) {
                            body.className = 'manage page settings list fixed-width';
                        }

                        return (
                            <SalesQuotasPageStandalone
                                search={routeProps.location.search}
                                pathname={`/setup/quota/view/${
                                    routeProps.match.params.quotaId || ''
                                }`}
                            />
                        );
                    }}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/dashboard'
                />
                <RestrictedRoute
                    path='/sales/sales-quotas/:quotaId'
                    render={(routeProps) => {
                        const body = document.querySelector('body');

                        setBodyId('quota-manager');

                        if (!NutClientConfig.hasQuotas) {
                            return <SalesQuotaFeatureGate />;
                        }

                        if (
                            NutClientConfig.hasQuotasV2FF &&
                            NutClientConfig.hasMigratedToQuotasV2
                        ) {
                            return <Redirect to='/sales/sales-quotas' />;
                        }

                        if (body) {
                            body.className = 'manage page settings list fixed-width';
                        }

                        return (
                            <SalesQuotasPageStandalone
                                search={routeProps.location.search}
                                pathname={`/setup/quota/${routeProps.match.params.quotaId || ''}`}
                            />
                        );
                    }}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/dashboard'
                />
                <RestrictedRoute
                    path='/sales/sales-quotas'
                    render={(routeProps) => {
                        const body = document.querySelector('body');

                        setBodyId('quota-manager');

                        if (!NutClientConfig.hasQuotas) {
                            return <SalesQuotaFeatureGate />;
                        }

                        if (
                            NutClientConfig.hasQuotasV2FF &&
                            NutClientConfig.hasMigratedToQuotasV2
                        ) {
                            const urlParams = new URLSearchParams(routeProps.location.search);
                            const fiscalYear = urlParams.get('fiscalYear');
                            const acceptedFiscalYears = getYears();
                            if (!fiscalYear || !acceptedFiscalYears.includes(Number(fiscalYear))) {
                                return (
                                    <Redirect
                                        to={`/sales/sales-quotas?fiscalYear=${getCurrentFiscalYear()}`}
                                    />
                                );
                            }
                            return (
                                <SalesQuotasPage
                                    activeTab={Number(fiscalYear)}
                                    history={routeProps.history}
                                />
                            );
                        }

                        if (body) {
                            body.className = 'manage page settings list fixed-width';
                        }

                        return (
                            <SalesQuotasPageStandalone
                                search={routeProps.location.search}
                                pathname='/setup/quota'
                            />
                        );
                    }}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/dashboard'
                />
                <RestrictedRoute
                    path='/sales/activity-quotas/:action?'
                    render={(routeProps) => {
                        const body = document.querySelector('body');

                        if (body) {
                            body.className = 'manage page settings list fixed-width';
                        }

                        setBodyId('');

                        return (
                            <ActivityQuotasPageStandalone
                                search={routeProps.location.search}
                                pathname={`/setup/activity-quota/${
                                    routeProps.match.params.action || ''
                                }`}
                            />
                        );
                    }}
                    hasAccess={canAccessSetup}
                    redirectPath='/sales/dashboard'
                />
            </Switch>
        </PageWrapper>
    );
}

function renderDashboard(canAccessCrm: boolean) {
    return (
        <RestrictedRoute
            path='/sales/dashboard'
            hasAccess={canAccessCrm}
            redirectPath='/marketing/dashboard'
            render={(routeProps) => {
                const urlParams = new URLSearchParams(routeProps.location.search);
                const tab = urlParams.get('tab');
                const resetActivities = urlParams.has('resetActivities');
                const tour = routeProps.location.state && routeProps.location.state.tour;

                return <MasterDashboard tab={tab} resetActivities={resetActivities} tour={tour} />;
            }}
        />
    );
}

function renderSchedulerPage() {
    return <SchedulerRoot />;
}

function renderProspectorIQPage(canUseProspectorIq: boolean) {
    return (
        <RestrictedListPageRoute
            path='/sales/prospectoriq'
            hasAccess={canUseProspectorIq}
            redirectPath='/sales/dashboard'
            // @ts-expect-error
            exact={true}
            localStorageKey='PROSPECTOR_VIEW_SEARCH'
            component={ProspectorListPage}
            defaultQuery='?filter%5BhasEmail%5D=true&filter%5BhasLinkedin%5D=true&filter%5BhasPhoneNumber%5D=true'
        />
    );
}

function renderSalesAutomationPage() {
    return <SalesAutomationRoot />;
}
